<template>
  <div>
    <div class="form-container ou-donation-form">
      <div class="holder">
        <PaymentForm class="form-horizontal">
          <main class="container donation-torah outorah-donate-form donation-torah--give">
            <h1 class="give-headline">Support the Torah You Learn Every Day</h1>
            <div class="give-layout">
            <aside class="give-intro">
              <p>
                OU Torah puts thousands of shiurim, articles, podcasts, and other Torah resources at
                your fingertips, from daily learning in Halacha, Mishna, Nach, and Parsha to classes
                and insights from leading speakers and educators.
              </p>
              <p>
                It has always been free, and that is not changing. We want Torah to be within reach
                whenever and wherever someone wants to learn.
              </p>
              <p>
                Your gift helps OU Torah continue to build this growing library of Torah, keeping it
                accessible to you and to the next person who comes looking for something to learn.
              </p>
              <p class="give-check">
                <em>
                  If you would like to donate by check, please make it out to:<br />
                  Orthodox Union<br />
                  Attn: Hannah Farkas<br />
                  40 Rector Street, 4th Floor<br />
                  New York, NY 10006<br />
                  Please note in the memo – OU Torah Campaign
                </em>
              </p>
            </aside>
            <div class="form-main">
              <section class="form-section">
                <header>
                  <h4>Your Gift</h4>
                </header>
                <div class="form__field">
                  <div class="custom-amount">
                    <p>Select amount</p>
                    <AmountSelector :includeOther="true" amountText="Amount" />
                    <ErrorLabel field="amount" />
                  </div>

                  <InHonorOf />
                  <br />

                  <FormRow>
                    <FormLabel class="control-label"> Special Instructions </FormLabel>
                  </FormRow>
                  <FormRow>
                    <FormInput
                      type="textarea"
                      class="width-full"
                      field="custom.comments"
                    />
                  </FormRow>
                </div>
              </section>
              <section class="form-section">
                <header>
                  <h4>Your contact information</h4>
                </header>
                <div class="form__field">
                  <FormRow inline>
                    <FormCol field="title" :flex="1" />
                    <FormCol field="firstName" :flex="2" />
                    <FormCol field="lastName" :flex="2" />
                  </FormRow>
                  <FormRow inline>
                    <FormCol field="email" />
                    <FormCol field="phone" />
                  </FormRow>
                  <FormRow inline>
                    <Consent privacy="https://www.ou.org/privacy-policy/"/>
                  </FormRow>
                </div>
              </section>
              <section class="form-section">
                <header>
                  <h4>Billing Information</h4>
                </header>
                <div class="form__field">
                  <DonorAddress />
                </div>
              </section>
              <section class="form-section">
                <header>
                  <h4>Payment Information</h4>
                </header>
                <div class="form__field">
                  <FormRow>
                    <Receipt :receipts="['us', 'israel', 'canadian']" />
                  </FormRow>
                  <br>
                  <FormRow inline>
                    <FormCol field="card" :showLabel="false" />
                  </FormRow>
                </div>
              </section>
              <FormRow>
                <NewsletterSubscribe
                  id="newsletterSubscription"
                  text="Sign me up for the OU's weekly Shabbat Shalom newsletter"
                />
              </FormRow>
              <FormRow>
                <input
                  type="checkbox"
                  id="planningSpecialist"
                  name="planningSpecialist"
                  @click="setValue('custom.planningSpecialist', $event.target.checked)"
                />
                <FormLabel for="planningSpecialist">
                  Please have the OU’s Legacy Planning Specialist reach out to me</FormLabel
                >
              </FormRow>
              <br />
              <PaymentButton showAmount btnText="Donate" />
            </div>
            </div>
            <footer class="give-footer">
              <p>
                Questions about your gift? Email
                <a href="mailto:donorservices@ou.org">donorservices@ou.org</a> or call
                <a href="tel:+12126138134">(212) 613-8134</a>, Monday through Thursday, 9 a.m. to
                5 p.m. ET.
              </p>
              <p>All gifts are tax-deductible.</p>
            </footer>
          </main>
        </PaymentForm>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, watch } from "vue";
import { usePaymentForm } from "~/shared/use-payment-form";

export default defineComponent({
  setup() {
    const { setValue, form, submit, testMode } = usePaymentForm();

    // admin.outorah.org callback + donor email read custom.dedicationMessage
    watch(
      [
        () => form.value.dedication,
        () => form.value.honoreeName,
        () => form.value.dedicationMessage,
      ],
      ([dedication, honoreeName, message]) => {
        const honoree = [dedication, honoreeName].filter(Boolean).join(" ");
        const value = honoreeName || message ? [honoree, message].filter(Boolean).join(": ") : null;
        if ((form.value.custom?.dedicationMessage ?? null) !== value) {
          setValue("custom.dedicationMessage", value);
        }
      }
    );

    setValue(
      "callback",
      testMode
        ? "https://admin-outorah.oudev.org/outorahDonationCallback"
        : "https://admin.outorah.org/outorahDonationCallback"
    );
    setValue("custom.seriesType", "General Donation");
    setValue("custom.programId", null);

    return {
      setValue,
      submit,
      form,
    };
  },
});
</script>
<style scoped lang="scss">
::v-deep .form-container.ou-donation-form {
  max-width: 100%;
  background: none;
  box-shadow: none;
  font-size: 16px;
  color: #222;
  line-height: 1.4;
  font-family: "proxima-nova-condensed", "Helvetica Neue", Helvetica, Arial, sans-serif;
  font-weight: 400;
  margin: 0;
  padding: 0;
}
::v-deep h2 {
  font-weight: 900;
  margin-bottom: 0;
  color: #4f1014;
  font-size: 20px;
  text-transform: capitalize;
  padding: 20px;
  margin-top: 0px;
}
::v-deep .radio-part {
  min-width: 31.33333% !important;
  max-width: 31.33333% !important;
  &:nth-child(3n) {
    padding-right: 0 !important;
  }
}
input[type="checkbox"] {
  margin-right: 5px;
}
.form-section {
  background: #f9f9f9;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  border-radius: 10px;
  margin-bottom: 30px;
  position: relative;
  padding-bottom: 15px;
  header {
    h4 {
      font-weight: 900;
      margin-bottom: 0px;
      color: #4f1014;
      margin-top: 0px;
      font-size: 20px;
      text-transform: capitalize;
      padding: 20px;
    }
    border-bottom: 1px solid #4f1014;
    margin-bottom: 20px;
  }
}

.form__field {
  padding: 20px;
}

.control-label {
  flex: 1;
  font-size: 14px;
  color: #222;
  max-width: 150px;
  padding-top: 3px;
  padding-bottom: 3px;
  display: block;
}
.sub__label {
  font-weight: 200;
  font-size: 14px;
  display: block;
  margin-top: 3px;
  margin-bottom: 8px;
}
.custom-amount {
  margin-bottom: 20px;

  p {
    margin: 0 0 18px;
    font-size: 16px;
  }
}

.container.donation-torah {
  padding: 20px 10px;
}

.give-headline {
  font-weight: 900;
  color: #4f1014;
  font-size: 34px;
  line-height: 1.2;
  text-align:center;
  margin: 0 0 50px;
}

.give-layout {
  display: flex;
  flex-direction: column;
  gap: 30px;

  @media only screen and (min-width: 900px) {
    display: grid;
    grid-template-columns: minmax(0, 600px) minmax(0, 400px);
    justify-content: center;
    gap: 60px;
    align-items: start;
  }
}

.give-intro {
  order: 2;
  font-size: 18px;
  line-height: 1.6;

  p {
    margin: 0 0 18px;
  }

  @media only screen and (min-width: 900px) {
    grid-column: 1;
    grid-row: 1;
    position: sticky;
    top: 20px;
  }
}

.form-main {
  order: 1;
  min-width: 0;

  @media only screen and (min-width: 900px) {
    grid-column: 2;
    grid-row: 1;
  }
}

::v-deep .ou-donation-btn {
  background: #4f1014;
  border-color: #4f1014;

  &:hover,
  &:active,
  &:focus {
    background: #3a0b0e;
    border-color: #3a0b0e;
  }
}

.give-footer {
  border-top: 1px solid #ddd;
  margin-top: 30px;
  padding-top: 20px;
  font-size: 15px;
  text-align: center;

  p {
    margin: 0 0 8px;
  }

  a {
    color: #4f1014;
  }
}
</style>
